import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useBrandManager } from '@/hooks/useBrandManager';
import { usePageVisibility } from '@/hooks/usePageVisibility';
export interface StackNotification {
  id: string;
  type: 'support_message' | 'business_metric';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  data?: {
    thread_id?: string;
    message_id?: string;
    metric_id?: string;
    metric_type?: string;
    [key: string]: any;
  };
  source?: string;
  icon?: string;
}

interface UseNotificationStackReturn {
  notifications: StackNotification[];
  allNotifications: StackNotification[];
  unreadCount: number;
  addNotification: (notification: Omit<StackNotification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  clearStack: () => void;
  isMinimized: boolean;
  toggleMinimized: () => void;
}

const MAX_VISIBLE_NOTIFICATIONS = 5;
const MAX_STORED_NOTIFICATIONS = 20; // Reduced from 50 to prevent localStorage overflow
const AUTO_DISMISS_TIMEOUT = 10000; // 10 seconds

export const useNotificationStack = (): UseNotificationStackReturn => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const { isVisible } = usePageVisibility();
  const [notifications, setNotifications] = useState<StackNotification[]>([]);
  const [isMinimized, setIsMinimized] = useState(true); // Collapsed by default
  const dismissTimers = useRef<Map<string, number>>(new Map());

  // Remove notification from stack
  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    
    // Clear dismiss timer
    const timer = dismissTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimers.current.delete(id);
    }
  }, []);

  // Add notification to stack
  const addNotification = useCallback((notification: Omit<StackNotification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: StackNotification = {
      ...notification,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      read: false,
    };

    setNotifications((prev) => {
      // Add to beginning of array
      const updated = [newNotification, ...prev];
      // Keep only last MAX_STORED_NOTIFICATIONS to prevent localStorage overflow
      return updated.slice(0, MAX_STORED_NOTIFICATIONS);
    });

    // Set auto-dismiss timer
    const timer = window.setTimeout(() => {
      removeNotification(newNotification.id);
    }, AUTO_DISMISS_TIMEOUT);

    dismissTimers.current.set(newNotification.id, timer);
  }, [removeNotification]);

  // Load notifications from localStorage on mount
  useEffect(() => {
    if (!user || !currentBrandId) return;

    const storageKey = `notification-stack-${user.id}-${currentBrandId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
      } catch (error) {
        console.error('Error loading notifications from storage:', error);
      }
    }
  }, [user, currentBrandId]);

  // Save notifications to localStorage with error handling
  useEffect(() => {
    if (!user || !currentBrandId) return;

    const storageKey = `notification-stack-${user.id}-${currentBrandId}`;
    
    // Only store essential fields to minimize storage usage
    const minimalNotifications = notifications.slice(0, MAX_STORED_NOTIFICATIONS).map(n => ({
      id: n.id,
      type: n.type,
      title: n.title,
      message: n.message?.slice(0, 200), // Truncate message
      timestamp: n.timestamp,
      read: n.read,
      data: n.data ? { 
        thread_id: n.data.thread_id,
        metric_id: n.data.metric_id,
      } : undefined, // Only keep essential data fields
    }));
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(minimalNotifications));
    } catch (error) {
      // Handle QuotaExceededError - clear old notification keys and retry
      console.warn('localStorage quota exceeded, clearing old notifications');
      
      // Find and remove all notification-stack keys for this user
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('notification-stack-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Retry with even fewer notifications
      try {
        localStorage.setItem(storageKey, JSON.stringify(minimalNotifications.slice(0, 5)));
      } catch {
        // Give up on storing notifications
        console.error('Failed to store notifications even after cleanup');
      }
    }
  }, [notifications, user, currentBrandId]);

  // Set up realtime subscriptions for support messages (visibility-aware)
  useEffect(() => {
    if (!user || !currentBrandId || !isVisible) return;

    let channel: any = null;

    // First, get thread IDs for this user and brand
    supabase
      .from('support_threads')
      .select('id')
      .eq('brand_id', currentBrandId)
      .eq('user_id', user.id)
      .then(({ data: threads }) => {
        if (!threads || threads.length === 0) return;

        const threadIds = threads.map(t => t.id);

        channel = supabase
          .channel(`push-notifications-support-${user.id}-${currentBrandId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'support_messages',
            },
            async (payload) => {
              const message = payload.new as any;
              
              // Check if this message is for one of the user's threads
              if (!threadIds.includes(message.thread_id)) return;
              
              // Skip if message is from the current user
              if (message.user_id === user.id) return;

              // Get thread info
              const { data: thread } = await supabase
                .from('support_threads')
                .select('subject, user_id')
                .eq('id', message.thread_id)
                .single();

              if (thread) {
                addNotification({
                  type: 'support_message',
                  title: 'New Support Message',
                  message: thread.subject || 'You have a new support message',
                  data: {
                    thread_id: message.thread_id,
                    message_id: message.id,
                  },
                  source: 'Support',
                });
              }
            }
          )
          .subscribe();
      });

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, currentBrandId, addNotification, isVisible]);

  // Set up realtime subscriptions for business metrics (visibility-aware)
  useEffect(() => {
    if (!user || !currentBrandId || !isVisible) return;

    const channel = supabase
      .channel(`push-notifications-metrics-${user.id}-${currentBrandId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'incoming_business_metrics',
          filter: `brand_id=eq.${currentBrandId}`,
        },
        (payload) => {
          const metric = payload.new as any;
          
          // Check if user should receive this notification
          // (This will be filtered by the trigger function, but we double-check here)
          addNotification({
            type: 'business_metric',
            title: 'New Business Metric',
            message: `New ${metric.type} recorded: ${metric.amount || metric.quantity || ''}`,
            data: {
              metric_id: metric.id,
              metric_type: metric.type,
              amount: metric.amount,
              quantity: metric.quantity,
              date: metric.date,
            },
            source: 'Business Metrics',
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, currentBrandId, addNotification, isVisible]);

  // Listen for browser push notifications
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event: MessageEvent) => {
        // Use requestIdleCallback to process notifications without blocking
        requestIdleCallback(() => {
          if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
            addNotification({
              type: event.data.data?.type || 'support_message',
              title: event.data.title,
              message: event.data.body,
              data: event.data.data,
              source: event.data.source,
              icon: event.data.icon,
            });
          }
        }, { timeout: 50 });
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, [addNotification]);

  // Mark notification as read
  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  // Clear all notifications
  const clearStack = useCallback(() => {
    // Clear all timers
    dismissTimers.current.forEach((timer) => clearTimeout(timer));
    dismissTimers.current.clear();
    
    setNotifications([]);
  }, []);

  // Toggle minimized state
  const toggleMinimized = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications: notifications.slice(0, MAX_VISIBLE_NOTIFICATIONS),
    allNotifications: notifications,
    unreadCount,
    addNotification,
    removeNotification,
    markAsRead,
    clearStack,
    isMinimized,
    toggleMinimized,
  };
};

